/**
 * Calculs métier corrosion : Ultra Mesure
 *
 * getDnFromDiametre et getEpaisseurOrigine utilisent les tables de nomenclatures
 * fournies par Renaud (mars 2026).
 */

import type { CodeCouleur } from "./types";
import { lookupDn, lookupEpaisseur, type LookupOptions } from "./nomenclatures";

/**
 * Calcule le pourcentage de corrosion.
 * Formule : ((épaisseur origine - mesure min) / épaisseur origine) × 100
 *
 * @returns pourcentage arrondi à 1 décimale, ou null si données manquantes
 */
export function calculateCorrosionPercent(
  mesureMin: number | null,
  epaisseurOrigine: number | null
): number | null {
  if (
    mesureMin === null ||
    epaisseurOrigine === null ||
    epaisseurOrigine <= 0
  ) {
    return null;
  }

  const percent = ((epaisseurOrigine - mesureMin) / epaisseurOrigine) * 100;
  return Math.round(percent * 10) / 10;
}

/**
 * Détermine le code couleur selon le pourcentage de corrosion.
 *
 * - green  : 0 à 19%      (bon état)
 * - orange : 19.1 à 29%   (surveillance)
 * - red    : 29.1 à 39.9% (alerte)
 * - dark   : ≥ 40%        (critique, bordeaux)
 *
 * Seuils validés par Renaud (avril 2026).
 */
export function getCodeCouleur(percent: number | null): CodeCouleur | null {
  if (percent === null) return null;

  // Corrosion négative = donnée incohérente (épaisseur min mesurée > épaisseur
  // d'origine, typiquement après un changement de matériau sur une mesure déjà
  // saisie). On ne classe PAS en "green" (sinon faux "bon état") : pas de pastille,
  // ce qui signale l'incohérence. Les seuils Renaud (19/29/40) restent intacts.
  if (percent < 0) return null;

  if (percent <= 19) return "green";
  if (percent <= 29) return "orange";
  if (percent < 40) return "red";
  return "dark";
}

/**
 * Recherche le DN à partir du diamètre extérieur via les tables de nomenclatures.
 *
 * - Acier noir/galva : cherche dans les tables tarif (T1, T19, T3, T10)
 * - Acier électrozingué : table COMAP XPress
 * - Acier inox : table ISO GAZ
 * - Cuivre : pas de DN (retourne null)
 * - Fonte : cherche par DE dans les tables fonte
 */
export function getDnFromDiametre(
  diametreExt: number | null,
  materiau?: string,
  tarification?: string[]
): number | null {
  if (diametreExt === null) return null;
  if (!materiau) return null;

  return lookupDn(diametreExt, materiau, tarification);
}

/**
 * Recherche l'épaisseur d'origine à partir du matériau et du DN.
 *
 * - Acier noir/galva : épaisseur selon tarification + ∅ ext
 * - Acier électrozingué : épaisseur par ∅ ext
 * - Cuivre : épaisseur par ∅ ext (1mm standard)
 * - Acier inox : retourne null (Renaud lit l'épaisseur sur le tuyau)
 * - Fonte : épaisseur selon DN + âge (Vieux/Récent) + type (SME/SMU)
 */
export function getEpaisseurOrigine(
  materiau: string,
  dn: number | null,
  options?: LookupOptions
): number | null {
  return lookupEpaisseur(materiau, dn, options);
}

/** Entrées brutes nécessaires au calcul dérivé d'une mesure. */
export interface MesureDerivedInput {
  diametreExt: number | null;
  mesureMin: number | null;
  materiau: string;
  /** Épaisseur saisie à la main, utilisée en fallback si la table ne renvoie rien (inox). */
  manualEpaisseur: number | null;
  /** true si l'épaisseur d'origine a été saisie à la main : elle prime alors
   *  toujours sur l'abaque (déverrouillage I13 demandé par Renaud, juillet 2026). */
  epaisseurManuelle?: boolean;
  /** DN saisi à la main (fonte) : prime sur le DN déduit du ∅ ext. */
  manualDn?: number | null;
  dnManuel?: boolean;
  tarification?: string[];
  fonteAge?: string;
  fonteType?: string;
}

/** Valeurs dérivées d'une mesure (DN, épaisseur, % corrosion, couleur). */
export interface MesureDerived {
  dn: number | null;
  /** Épaisseur calculée par les tables (null si aucune → cas inox, lecture manuelle). */
  autoEpaisseur: number | null;
  /** Épaisseur réellement utilisée pour le calcul du % : auto si dispo, sinon manuelle. */
  effectiveEpaisseur: number | null;
  pourcentageCorrosion: number | null;
  codeCouleur: CodeCouleur | null;
}

/**
 * Orchestration métier pure : DN → épaisseur d'origine → % corrosion → code couleur.
 *
 * Extrait de `recalcAndSave` (mesure-form) pour être testable hors React (I10).
 *
 * Priorités (arbitrage Renaud, juillet 2026, remplace le verrou I13) :
 * - `epaisseurManuelle` : la saisie manuelle prime toujours sur l'abaque.
 * - Sinon l'abaque prime. Si l'abaque ne trouve rien :
 *   - Fonte : épaisseur null (« hors abaque »), pas de valeur fantôme conservée.
 *     Les anciennes mesures fonte n'ont que des valeurs auto (la saisie manuelle
 *     y était impossible), les vider est donc toujours correct.
 *   - Autres matériaux (inox, acier hors nomenclature) : fallback sur la saisie
 *     manuelle, comportement historique conservé.
 * - `dnManuel` (fonte) : le DN saisi prime sur le DN déduit du ∅ ext.
 */
export function computeMesureDerived(input: MesureDerivedInput): MesureDerived {
  const dnFromDiametre = getDnFromDiametre(input.diametreExt, input.materiau, input.tarification);
  const dn =
    input.dnManuel && input.manualDn !== null && input.manualDn !== undefined
      ? input.manualDn
      : dnFromDiametre;

  const autoEpaisseur = getEpaisseurOrigine(input.materiau, dn, {
    diametreExt: input.diametreExt,
    tarification: input.tarification,
    fonteAge: input.fonteAge || undefined,
    fonteType: input.fonteType || undefined,
  });

  let effectiveEpaisseur: number | null;
  if (input.epaisseurManuelle) {
    effectiveEpaisseur = input.manualEpaisseur;
  } else if (autoEpaisseur !== null) {
    effectiveEpaisseur = autoEpaisseur;
  } else if (input.materiau === "Fonte") {
    effectiveEpaisseur = null;
  } else {
    effectiveEpaisseur = input.manualEpaisseur;
  }

  const pourcentageCorrosion = calculateCorrosionPercent(input.mesureMin, effectiveEpaisseur);
  const codeCouleur = getCodeCouleur(pourcentageCorrosion);

  return { dn, autoEpaisseur, effectiveEpaisseur, pourcentageCorrosion, codeCouleur };
}
